import { useState } from 'react'

import {
  Box,
  Button,
  Icon,
  Text,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import { FormikProvider } from 'formik'
import {
  AtsCreateJobPostingEvaluationFormDocument,
  AtsGetJobPostingSettingsEvaluationFormsDocument,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  EvaluationFormEditor,
  NEW_EVALUATION_FROM_SCRATCH,
} from 'modules/ats/components/EvaluationForm/components/EvaluationFormEditor'
import {
  buildEmptyQuestion,
  useEvaluationFormForm,
} from 'modules/ats/components/EvaluationForm/useEvaluationFormForm'

import { AddEvaluationFormSelector } from './AddEvaluationFormSelector'

interface Props {
  jobId: number
  onClose: () => void
}

export const AddEvaluationForm = ({ jobId, onClose }: Props) => {
  const [showEditor, setShowEditor] = useState(false)

  const session = useCurrentSession()
  const { openDialog } = useConfirmationDialog()

  const [create, { loading }] = useMutation(AtsCreateJobPostingEvaluationFormDocument, {
    extractorFn: (data) => data.atsMutations.createAtsEvaluationForm,
    refetchQueries: [AtsGetJobPostingSettingsEvaluationFormsDocument],
  })

  const { form, hasInvalidQuestions } = useEvaluationFormForm({
    defaultValues: {
      atsJobPostingId: jobId,
      name: '',
      questions: [buildEmptyQuestion()],
    },

    async onSubmit(data, { setErrors }) {
      track(trackingEvents.ats.jobSettingsAddEvaluationForm)

      const result = await create({
        variables: {
          companyId: session.company.id,
          atsJobPostingId: jobId,
          name: data.name,
          questions: data.questions.map((question) => ({
            id: question.id,
            description: question.description,
            text: question.text,
          })),
        },
      })
      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  const onSelectNewEvaluationOption = (value: string | null) => {
    form.resetForm()
    setShowEditor(true)
    form.setFieldValue(
      'basedOnId',
      value === NEW_EVALUATION_FROM_SCRATCH || value === null ? undefined : Number(value)
    )
  }

  return (
    <FormikProvider value={form}>
      <Sidepanel
        open
        width="wide"
        title={i18n.t('ats.job_posting_settings.evaluation_forms.new_modal.title')}
        footer={
          <Box flexDirection="row" justifyContent="spaceBetween" width="full" alignItems="center">
            {form.dirty ? (
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Icon.Medium icon="alert-circle-line" color="grey800" />
                <Text.Normal size="200">
                  {i18n.t('ats.job_posting_settings.evaluation_forms.form.warning')}
                </Text.Normal>
              </Box>
            ) : (
              <div />
            )}

            <Box flexDirection="row" gap="s8" alignSelf="flexEnd">
              <Button
                onClick={() => {
                  form.dirty
                    ? openDialog({
                        title: i18n.t(
                          'ats.job_posting_settings.evaluation_forms.form.unsaved_changes.title'
                        ),
                        illustration: 'alert',
                        content: i18n.t(
                          'ats.job_posting_settings.evaluation_forms.form.unsaved_changes.description'
                        ),
                        confirmLabel: i18n.t('common.cancel'),
                        cancelLabel: i18n.t(
                          'ats.job_posting_settings.evaluation_forms.form.unsaved_changes.continue'
                        ),
                        onConfirm: onClose,
                      })
                    : onClose()
                }}
                type="secondary"
              >
                {i18n.t('common.cancel')}
              </Button>
              <Button onClick={() => form.handleSubmit()} loading={loading || !showEditor}>
                {i18n.t('common.save')}
              </Button>
            </Box>
          </Box>
        }
        onClose={onClose}
        closeLabel={i18n.t('common.close')}
      >
        <Box gap="s16">
          <AddEvaluationFormSelector
            onSelectOption={(value) => onSelectNewEvaluationOption(value)}
          />

          {showEditor && <EvaluationFormEditor hasInvalidQuestions={hasInvalidQuestions} />}
        </Box>
      </Sidepanel>
    </FormikProvider>
  )
}
